const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "api", "saleor"]);

// Nur diese Pfade gehören zu Saleor (MEDIA_URL bzw. Thumbnail-Endpoint).
// Alles andere unter "/" ist ein Storefront-Asset aus /public (z.B. /peptides/*.png).
const SALEOR_MEDIA_PATH_PREFIXES = ["/media/", "/thumbnail/"];

/**
 * Origin embedded in HTML/CSS for browsers and for `next/image` `src` / preload hints.
 *
 * Never use Docker-only hostnames like `http://api:8000` here — the browser cannot resolve `api`.
 * For local compose, set `NEXT_PUBLIC_SALEOR_IMAGE_ORIGIN=http://localhost:8000` (or your public API host).
 *
 * `SALEOR_IMAGE_ORIGIN` is for server-to-Saleor HTTP (GraphQL, scripts), not for media URLs in the DOM.
 */
function getNormalizedOrigin(): string {
	return process.env.NEXT_PUBLIC_SALEOR_IMAGE_ORIGIN || "http://localhost:8000";
}

function isSaleorMediaPath(pathname: string): boolean {
	return SALEOR_MEDIA_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Normalize Saleor media URLs for use in the storefront.
 * - Relative Saleor URLs (/media/..., /thumbnail/...) → prepend configured origin
 * - Other relative paths (storefront /public assets) → unchanged
 * - Local/dev origins (localhost, api, saleor in Docker) → replace with configured origin,
 *   or strip back to a relative path when they point at a /public asset (stale localStorage)
 * - Saleor Cloud (api.saleor.cloud, *.saleor.cloud) → pass through unchanged
 */
export function normalizeSaleorMediaUrl(url: string | null | undefined): string {
	if (!url) return "";
	if (url.startsWith("//")) {
		return `https:${url}`;
	}

	if (url.startsWith("/")) {
		return isSaleorMediaPath(url) ? `${getNormalizedOrigin()}${url}` : url;
	}

	try {
		const parsed = new URL(url);
		const hostname = parsed.hostname.toLowerCase();
		// Only rewrite if hostname is exactly a local/dev host (not api.saleor.cloud etc.)
		if (LOCAL_HOSTNAMES.has(hostname)) {
			const path = parsed.pathname + parsed.search;
			if (!isSaleorMediaPath(parsed.pathname)) {
				return path;
			}
			const origin = getNormalizedOrigin().replace(/\/$/, "");
			return `${origin}${path}`;
		}
	} catch {
		// Invalid URL, return as-is
	}

	return url;
}
